import { api } from "@/api"
import type { MaybeRef } from "@vueuse/core"
import { useQuery } from "@tanstack/vue-query"
import type {
  TRANSACTION_METHOD,
  TransactionSignatureStatus,
} from "../sharedTypes/transactions"

export type OrderDetailOrderItem = {
  id: string
  product: {
    id: string
    hasVariablePrice: boolean
    name: string
    color: string | null
  } | null
  productModifierLabels: Array<string> | null
  price: number
  note: string | null
}

export type OrderDetailRefund = {
  id: string
  type: TransactionType
  createdAt: Date
  invoiceId: string
  parentTransactionId: string | null
  amountTransactedGross: number
  amountTransactedGratuity: number
  amountTransactedTotal: number
  currency: string
  transactionMethod: string
  uti: string | null
  status: TransactionStatus
  deviceSerialNumber: string | null
  isDcc: boolean
  isDccFieldsPopulated: boolean
  dccCardCurrencyAmount: number
  dccCardholderCurrency: string
}

type OrderDetailPaymentBase = {
  id: string
  type: TransactionType
  amountTransactedGross: number
  amountTransactedGratuity: number
  amountCashback: number
  amountTransactedTotal: number
  currency: string
  createdAt: Date
  invoiceId: string
  refunds: Array<OrderDetailRefund>
  status: TransactionStatus
  deviceSerialNumber: string | null
}

type OrderDetailPaymentCard = OrderDetailPaymentBase & {
  transactionMethod: (typeof TRANSACTION_METHOD)["POS_CARD"]
  cardScheme: string | null
  cardNumberLast4: string
  acquirerResponseCode: string | null
  amountCashback: number
  signatureStatus: TransactionSignatureStatus | null
  retrievalReferenceNumber: string | null
  uti: string | null
  isDcc: boolean
  isDccFieldsPopulated: boolean
  dccCardCurrencyAmount: number
  dccCardholderCurrency: string
}

type OrderDetailPaymentCash = OrderDetailPaymentBase & {
  transactionMethod: (typeof TRANSACTION_METHOD)["POS_CASH"]
  cashTendered: number
  changeGiven: number
}

type OrderDetailPaymentUnknown = OrderDetailPaymentBase & {
  transactionMethod: null
}

export type OrderDetailPayment =
  | OrderDetailPaymentCard
  | OrderDetailPaymentCash
  | OrderDetailPaymentUnknown

type OrderDetailInvoice = {
  id: string
  amountGross: number
  createdAt: Date
  isPaid: boolean
  paidAt: Date | null
  paidBy:
    | PaymentDetailCard
    | PaymentDetailCash
    | PaymentDetailSplit
    | null
  amountBalancedGross: number
  amountBalancedGratuity: number
  amountBalancedTotal: number
}

type OrderDetail = {
  id: string
  note: string | null
  createdAt: Date
  totalCost: number
  cashierId: string
  invoice: OrderDetailInvoice | null
  orderItems: Array<OrderDetailOrderItem>
  payments: Array<OrderDetailPayment>
  orderNumber: number | null

  serviceChargeTotal: number
  serviceChargeFlat: number
  serviceChargePercentage: number
}

type RetrieveOrderResponse = {
  order: RecursivelyConvertDatesToStrings<OrderDetail>
  receiptString: string
}

const parseOrderDetail = (
  order: RecursivelyConvertDatesToStrings<OrderDetail>
): OrderDetail => ({
  ...order,
  createdAt: DateFns.parseISO(order.createdAt),
  invoice: order.invoice && {
    ...order.invoice,
    createdAt: DateFns.parseISO(order.invoice.createdAt),
    paidAt: order.invoice.paidAt
      ? DateFns.parseISO(order.invoice.paidAt)
      : null,
  },
  payments: order.payments.map((payment) => ({
    ...payment,
    createdAt: DateFns.parseISO(payment.createdAt),
    refunds: payment.refunds.map((refund) => ({
      ...refund,
      createdAt: DateFns.parseISO(refund.createdAt),
    })),
  })),
})

export const retrieveOrder = async (orderId: string) => {
  const { body } = await api.get<RetrieveOrderResponse>(
    `/orders/${orderId}`
  )

  return {
    ...parseOrderDetail(body.order),
    receiptString: body.receiptString,
  }
}

export const useOrder = (orderId: MaybeRef<string>) =>
  useQuery(
    [BASE_ORDERS_QUERY_KEY, orderId],
    ({ queryKey: [_name, orderId_] }) =>
      retrieveOrder(unref(orderId_)),
    { refetchInterval: 60 * 1000 }
  )
